import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { DashboardsPageFrame, HorizonTabs } from "./dashboardKit";
import {
  enrollmentBookingSourcesQueryOptions,
  enrollmentCohortCurvesQueryOptions,
  enrollmentCohortStatusOptionsQueryOptions,
  enrollmentCohortStatusQueryOptions,
  enrollmentMonthlyConversionQueryOptions,
  enrollmentEodQueryOptions,
  enrollmentEsConversionQueryOptions,
  enrollmentEsOpsCallsQueryOptions,
  enrollmentEsOpsQueryOptions,
  enrollmentNeuroAppointmentsQueryOptions,
  enrollmentReferralsQueryOptions,
  enrollmentSnapshotQueryOptions,
  enrollmentSnapshotsQueryOptions,
  enrollmentTrendQueryOptions,
  fetchEnrollmentEod,
  fetchEnrollmentSnapshot,
} from "./dashboardsReactQuery";
import { EnrollmentCallReviewsTab } from "./enrollmentCallReviewsTab";
import { EnrollmentCohortCurvesTab } from "./enrollmentCohortCurvesTab";
import { EnrollmentCohortStatusSection } from "./enrollmentCohortStatusSection";
import { cohortStatusFilters } from "./enrollmentCohortStatus";
import { EnrollmentEodTab } from "./enrollmentEodTab";
import { EnrollmentEsConversionTab } from "./enrollmentEsConversionTab";
import { EnrollmentMonthlyTab } from "./enrollmentMonthlyTab";
import { EnrollmentMonthlyConversionSections } from "./enrollmentMonthlyConversionSections";
import { EnrollmentReferralsTab } from "./enrollmentReferralsTab";
import {
  CALL_REVIEW_MAX_RANGE_DAYS,
  completedMonthOptions,
  comparisonConfig,
  isoDatesInclusive,
  monthStartIso,
  recentAnalysisDates,
  shiftIsoDate,
  todayIso,
  useDebouncedValue,
} from "./enrollmentShared";
import type { EnrollmentConversionDetail, EnrollmentSnapshotMetrics } from "./enrollmentTypes";
import { EnrollmentWeeklyTab } from "./enrollmentWeeklyTab";

const TAB_OPTIONS = [
  { value: "eod", label: "EOD Dash" },
  { value: "referralsAppointments", label: "Referrals & appointments" },
  { value: "weekly", label: "ES Ops dashboards" },
  { value: "esConversion", label: "ES conversion" },
  { value: "monthly", label: "Monthly" },
  { value: "cohortCurves", label: "Cohort curves" },
  { value: "callReviews", label: "Call reviews" },
] as const;

type EnrollmentTab = (typeof TAB_OPTIONS)[number]["value"];

async function loadEnrollmentConversionRows(
  start: string,
  end: string,
): Promise<EnrollmentConversionDetail[]> {
  const dates = isoDatesInclusive(start, end);
  if (dates.length > CALL_REVIEW_MAX_RANGE_DAYS) {
    throw new Error(`Date range cannot exceed ${CALL_REVIEW_MAX_RANGE_DAYS} days.`);
  }
  const dailyRows = new Array<Awaited<ReturnType<typeof fetchEnrollmentEod>>>(dates.length);
  let nextIndex = 0;
  const workerCount = Math.min(5, dates.length);

  async function loadNextDate() {
    while (nextIndex < dates.length) {
      const index = nextIndex;
      nextIndex += 1;
      const date = dates[index];
      if (!date) continue;
      dailyRows[index] = await fetchEnrollmentEod({
        date,
        includeReferrals: false,
      });
    }
  }

  await Promise.all(Array.from({ length: workerCount }, loadNextDate));
  return dailyRows.flatMap((day) => day?.data.conversionRows || []);
}

export function EnrollmentDashboard() {
  const neuroAnchorDate = useMemo(() => todayIso(), []);
  const defaultReviewRange = useMemo(() => {
    const yesterday = shiftIsoDate(todayIso(), -1, "day");
    return { start: yesterday, end: yesterday };
  }, []);
  const monthlyOptions = useMemo(() => completedMonthOptions(todayIso(), 6), []);

  const [activeTab, setActiveTab] = useState<EnrollmentTab>("eod");
  const [cohortStatusClinicIds, setCohortStatusClinicIds] = useState<string[] | null>(null);
  const [cohortStatusMonth, setCohortStatusMonth] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const requestDate = useDebouncedValue(selectedDate, 150);
  const [esOpsDate, setEsOpsDate] = useState(todayIso());
  const [monthlySelectedDate, setMonthlySelectedDate] = useState(
    monthlyOptions[0]?.value ?? todayIso(),
  );
  const [callReviewStart, setCallReviewStart] = useState(defaultReviewRange.start);
  const [callReviewEnd, setCallReviewEnd] = useState(defaultReviewRange.end);

  const effectiveSnapshotDate = activeTab === "monthly" ? monthlySelectedDate : requestDate;
  const { unit: comparisonUnit, label: comparisonLabel } = comparisonConfig(activeTab);
  const comparisonDate = effectiveSnapshotDate
    ? shiftIsoDate(effectiveSnapshotDate, -1, comparisonUnit)
    : null;

  const eodRequestDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => shiftIsoDate(requestDate, index - 6, "day")),
    [requestDate],
  );
  const snapshotRequestDates = useMemo(
    () => [...new Set([monthStartIso(requestDate), ...eodRequestDates])].sort(),
    [eodRequestDates, requestDate],
  );

  const eodSnapshotQuery = useQuery({
    ...enrollmentSnapshotQueryOptions(requestDate),
    enabled: activeTab === "eod",
  });
  const pairSnapshotQuery = useQuery({
    queryKey: ["enrollment", "pair-snapshot", effectiveSnapshotDate, comparisonDate, activeTab],
    enabled:
      Boolean(effectiveSnapshotDate) &&
      activeTab !== "eod" &&
      activeTab !== "referralsAppointments" &&
      activeTab !== "callReviews" &&
      activeTab !== "cohortCurves" &&
      activeTab !== "esConversion",
    queryFn: async () => {
      const [current, previous] = await Promise.all([
        fetchEnrollmentSnapshot(effectiveSnapshotDate),
        comparisonDate ? fetchEnrollmentSnapshot(comparisonDate) : Promise.resolve(null),
      ]);
      return {
        snapshot: current.data,
        comparisonSnap: previous?.data ?? null,
      };
    },
  });

  const eodCallsQuery = useQuery({
    ...enrollmentEodQueryOptions({ date: requestDate, section: "calls" }),
    enabled: activeTab === "eod",
  });
  const eodConversionQuery = useQuery({
    ...enrollmentEodQueryOptions({ date: requestDate, section: "conversion" }),
    enabled: activeTab === "eod",
  });
  const eodCumulativeQuery = useQuery({
    ...enrollmentEodQueryOptions({ date: requestDate, section: "cumulative" }),
    enabled: activeTab === "eod",
  });
  const eodSnapshotsQuery = useQuery({
    ...enrollmentSnapshotsQueryOptions(snapshotRequestDates.filter((date) => date !== requestDate)),
    enabled:
      activeTab === "eod" &&
      !eodSnapshotQuery.isPending &&
      !eodCallsQuery.isPending &&
      !eodConversionQuery.isPending &&
      !eodCumulativeQuery.isPending,
  });
  const referralsQuery = useQuery({
    ...enrollmentReferralsQueryOptions(requestDate),
    enabled: activeTab === "referralsAppointments",
  });
  const bookingQuery = useQuery({
    ...enrollmentBookingSourcesQueryOptions(neuroAnchorDate),
    enabled: activeTab === "referralsAppointments",
  });
  const neuroQuery = useQuery({
    ...enrollmentNeuroAppointmentsQueryOptions(neuroAnchorDate),
    enabled: activeTab === "referralsAppointments",
  });
  const esOpsQuery = useQuery({
    ...enrollmentEsOpsQueryOptions(esOpsDate),
    enabled: activeTab === "weekly",
  });
  const esOpsCallsQuery = useQuery({
    ...enrollmentEsOpsCallsQueryOptions(esOpsDate),
    enabled: activeTab === "weekly",
  });
  const esConversionQuery = useQuery({
    ...enrollmentEsConversionQueryOptions(neuroAnchorDate),
    enabled: activeTab === "esConversion",
  });
  const trendQuery = useQuery({
    ...enrollmentTrendQueryOptions({ window: "monthly" }),
    enabled: activeTab === "monthly",
  });
  const cohortCurvesQuery = useQuery({
    ...enrollmentCohortCurvesQueryOptions(),
    enabled: activeTab === "cohortCurves",
  });
  const cohortStatusOptionsQuery = useQuery({
    ...enrollmentCohortStatusOptionsQueryOptions(),
    enabled: activeTab === "cohortCurves",
  });
  const statusFilters = cohortStatusFilters(
    cohortStatusOptionsQuery.data,
    cohortStatusClinicIds,
    cohortStatusMonth,
  );
  const cohortStatusQuery = useQuery({
    ...enrollmentCohortStatusQueryOptions(statusFilters.month, statusFilters.ids),
    enabled:
      activeTab === "cohortCurves" && Boolean(statusFilters.month) && statusFilters.ids.length > 0,
  });
  const monthlyConversionQuery = useQuery({
    ...enrollmentMonthlyConversionQueryOptions(),
    enabled: activeTab === "cohortCurves",
  });
  const monthlySnapshotQueries = useQueries({
    queries: monthlyOptions.map((option) => ({
      ...enrollmentSnapshotQueryOptions(option.value),
      enabled: activeTab === "monthly",
    })),
  });
  const callReviewsQuery = useQuery({
    queryKey: ["enrollment", "call-reviews", callReviewStart, callReviewEnd],
    enabled:
      activeTab === "callReviews" &&
      Boolean(callReviewStart) &&
      Boolean(callReviewEnd) &&
      callReviewStart <= callReviewEnd,
    queryFn: () => loadEnrollmentConversionRows(callReviewStart, callReviewEnd),
  });

  const snapshot: EnrollmentSnapshotMetrics | null =
    activeTab === "eod"
      ? (eodSnapshotQuery.data?.data ?? null)
      : (pairSnapshotQuery.data?.snapshot ?? null);
  const comparisonSnap: EnrollmentSnapshotMetrics | null =
    activeTab === "eod"
      ? (eodSnapshotsQuery.data?.snapshots.find((row) => row.date === comparisonDate)?.data ?? null)
      : (pairSnapshotQuery.data?.comparisonSnap ?? null);
  const snapshotHistoryRows = snapshotRequestDates.map((date) =>
    date === requestDate
      ? { date, data: eodSnapshotQuery.data?.data ?? null }
      : (eodSnapshotsQuery.data?.snapshots.find((row) => row.date === date) ?? {
          date,
          data: null,
        }),
  );

  const recentDates = useMemo(() => recentAnalysisDates(), []);

  const esOpsDates = useMemo(
    () => Array.from({ length: 10 }, (_, index) => shiftIsoDate(todayIso(), -index, "day")),
    [],
  );

  const filteredMonthlyRows = useMemo(() => {
    const monthStarts = new Set(monthlyOptions.map((option) => monthStartIso(option.value)));
    return (trendQuery.data?.rows || []).filter((row) => monthStarts.has(row.periodStart));
  }, [monthlyOptions, trendQuery.data?.rows]);

  const monthlySnapshotRows = monthlyOptions
    .slice()
    .reverse()
    .map((option, index) => {
      const queryIndex = monthlyOptions.length - 1 - index;
      const snap = monthlySnapshotQueries[queryIndex]?.data?.data;
      return {
        label: option.label,
        aligned: snap?.aligned ?? 0,
        marketablePatients: snap?.marketablePatients ?? 0,
      };
    });

  const snapLoading =
    activeTab === "eod" ? eodSnapshotQuery.isLoading : pairSnapshotQuery.isLoading;
  const snapError =
    activeTab === "eod"
      ? eodSnapshotQuery.error instanceof Error
        ? eodSnapshotQuery.error.message
        : eodSnapshotQuery.error
          ? "Failed to load snapshot"
          : ""
      : pairSnapshotQuery.error instanceof Error
        ? pairSnapshotQuery.error.message
        : pairSnapshotQuery.error
          ? "Failed to load snapshot"
          : "";

  return (
    <DashboardsPageFrame className="flex flex-col gap-6">
      <header className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Outreach & enrollment
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Enrollment</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            GUIDE enrollment EOD snapshot, conversion monitoring, monthly cumulative rollups, and
            referral tracking.
          </p>
        </div>
        <HorizonTabs
          idPrefix="enrollment"
          options={[...TAB_OPTIONS]}
          value={activeTab}
          onChange={(value) => setActiveTab(value as EnrollmentTab)}
        />
      </header>

      <section
        id="enrollment-panel-eod"
        role="tabpanel"
        aria-labelledby="enrollment-tab-eod"
        hidden={activeTab !== "eod"}
      >
        <EnrollmentEodTab
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          recentDates={recentDates}
          snapLoading={snapLoading}
          snapError={snapError}
          snapshot={snapshot}
          comparisonSnap={comparisonSnap}
          comparisonLabel={comparisonLabel}
          snapshotHistoryRows={snapshotHistoryRows}
          snapshotHistoryLoading={eodSnapshotsQuery.isPending}
          snapshotHistoryError={
            eodSnapshotsQuery.error?.message ||
            (eodSnapshotsQuery.data?.snapshots.some((row) => row.error)
              ? "Some snapshot dates are unavailable. Missing values are shown as —."
              : "")
          }
          calls={eodCallsQuery.data?.data.calls}
          callsLoading={eodCallsQuery.isPending}
          callsError={eodCallsQuery.error?.message ?? ""}
          conversionRows={eodConversionQuery.data?.data.conversionRows ?? []}
          conversionLoading={eodConversionQuery.isPending}
          conversionError={eodConversionQuery.error?.message ?? ""}
          cumulativeRows={eodCumulativeQuery.data?.data.cumulativeRows ?? []}
          cumulativeLoading={eodCumulativeQuery.isPending}
          cumulativeError={eodCumulativeQuery.error?.message ?? ""}
          refreshing={
            eodSnapshotQuery.isFetching ||
            eodSnapshotsQuery.isFetching ||
            eodCallsQuery.isFetching ||
            eodConversionQuery.isFetching ||
            eodCumulativeQuery.isFetching
          }
          onRefresh={() => {
            void Promise.all([
              eodSnapshotQuery.refetch(),
              eodCallsQuery.refetch(),
              eodConversionQuery.refetch(),
              eodCumulativeQuery.refetch(),
            ]).then(() => eodSnapshotsQuery.refetch());
          }}
        />
      </section>

      <section
        id="enrollment-panel-referralsAppointments"
        role="tabpanel"
        aria-labelledby="enrollment-tab-referralsAppointments"
        hidden={activeTab !== "referralsAppointments"}
      >
        <EnrollmentReferralsTab
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          recentDates={recentDates}
          neuroAnchorDate={neuroAnchorDate}
          bookingLoading={bookingQuery.isLoading}
          bookingError={
            bookingQuery.error instanceof Error
              ? bookingQuery.error.message
              : bookingQuery.error
                ? "Failed"
                : ""
          }
          bookingRows={bookingQuery.data?.rows ?? []}
          bookingClinics={bookingQuery.data?.clinics ?? []}
          neuroLoading={neuroQuery.isLoading}
          neuroError={
            neuroQuery.error instanceof Error
              ? neuroQuery.error.message
              : neuroQuery.error
                ? "Failed"
                : ""
          }
          neuroRows={neuroQuery.data?.rows ?? []}
          neuroClinics={neuroQuery.data?.clinics ?? []}
          referralLoading={referralsQuery.isLoading}
          referralError={
            referralsQuery.error instanceof Error
              ? referralsQuery.error.message
              : referralsQuery.error
                ? "Failed"
                : ""
          }
          referralRows={referralsQuery.data?.referrals ?? []}
          referralClinics={referralsQuery.data?.clinics ?? []}
        />
      </section>

      <section
        id="enrollment-panel-weekly"
        role="tabpanel"
        aria-labelledby="enrollment-tab-weekly"
        hidden={activeTab !== "weekly"}
      >
        <EnrollmentWeeklyTab
          esOpsDate={esOpsDate}
          setEsOpsDate={setEsOpsDate}
          esOpsDates={esOpsDates}
          loading={esOpsQuery.isLoading || esOpsCallsQuery.isLoading}
          error={
            esOpsQuery.error instanceof Error
              ? esOpsQuery.error.message
              : esOpsCallsQuery.error instanceof Error
                ? esOpsCallsQuery.error.message
                : esOpsQuery.error || esOpsCallsQuery.error
                  ? "Failed"
                  : ""
          }
          rows={esOpsQuery.data?.rows ?? []}
          callRows={esOpsCallsQuery.data?.rows ?? []}
        />
      </section>

      <section
        id="enrollment-panel-esConversion"
        role="tabpanel"
        aria-labelledby="enrollment-tab-esConversion"
        hidden={activeTab !== "esConversion"}
      >
        <EnrollmentEsConversionTab
          analysisDate={esConversionQuery.data?.date ?? neuroAnchorDate}
          loading={esConversionQuery.isLoading}
          error={
            esConversionQuery.error instanceof Error
              ? esConversionQuery.error.message
              : esConversionQuery.error
                ? "Failed"
                : ""
          }
          rows={esConversionQuery.data?.rows ?? []}
        />
      </section>

      <section
        id="enrollment-panel-monthly"
        role="tabpanel"
        aria-labelledby="enrollment-tab-monthly"
        hidden={activeTab !== "monthly"}
      >
        <EnrollmentMonthlyTab
          monthlyOptions={monthlyOptions}
          monthlySelectedDate={monthlySelectedDate}
          setMonthlySelectedDate={setMonthlySelectedDate}
          effectiveSnapshotDate={effectiveSnapshotDate}
          comparisonLabel={comparisonLabel}
          snapLoading={snapLoading}
          snapError={snapError}
          snapshot={snapshot}
          comparisonSnap={comparisonSnap}
          monthlySnapshotLoading={monthlySnapshotQueries.some((query) => query.isLoading)}
          monthlySnapshotError={
            monthlySnapshotQueries.find((query) => query.error)?.error instanceof Error
              ? (monthlySnapshotQueries.find((query) => query.error)?.error as Error).message
              : monthlySnapshotQueries.some((query) => query.error)
                ? "Failed"
                : ""
          }
          monthlySnapshotRows={monthlySnapshotRows}
          filteredMonthlyRows={filteredMonthlyRows}
          trendLoading={trendQuery.isLoading}
          trendError={
            trendQuery.error instanceof Error
              ? trendQuery.error.message
              : trendQuery.error
                ? "Failed"
                : ""
          }
        />
      </section>

      <section
        id="enrollment-panel-cohortCurves"
        role="tabpanel"
        aria-labelledby="enrollment-tab-cohortCurves"
        hidden={activeTab !== "cohortCurves"}
      >
        {activeTab === "cohortCurves" ? (
          <>
            <EnrollmentCohortStatusSection
              options={cohortStatusOptionsQuery.data}
              data={cohortStatusQuery.data}
              clinicIds={statusFilters.ids}
              month={statusFilters.month}
              months={statusFilters.months}
              onClinicsChange={setCohortStatusClinicIds}
              onMonthChange={setCohortStatusMonth}
              loading={cohortStatusOptionsQuery.isLoading || cohortStatusQuery.isLoading}
              error={
                cohortStatusOptionsQuery.error instanceof Error
                  ? cohortStatusOptionsQuery.error.message
                  : cohortStatusQuery.error instanceof Error
                    ? cohortStatusQuery.error.message
                    : ""
              }
              onRetry={() => {
                void cohortStatusOptionsQuery.refetch();
                if (statusFilters.month && statusFilters.ids.length)
                  void cohortStatusQuery.refetch();
              }}
            />
            <EnrollmentCohortCurvesTab
              loading={cohortCurvesQuery.isLoading}
              error={
                cohortCurvesQuery.error instanceof Error ? cohortCurvesQuery.error.message : ""
              }
              data={cohortCurvesQuery.data}
              onRetry={() => void cohortCurvesQuery.refetch()}
            />
            <EnrollmentMonthlyConversionSections
              data={monthlyConversionQuery.data}
              loading={monthlyConversionQuery.isLoading}
              error={
                monthlyConversionQuery.error instanceof Error
                  ? monthlyConversionQuery.error.message
                  : ""
              }
              onRetry={() => void monthlyConversionQuery.refetch()}
            />
          </>
        ) : null}
      </section>

      <section
        id="enrollment-panel-callReviews"
        role="tabpanel"
        aria-labelledby="enrollment-tab-callReviews"
        hidden={activeTab !== "callReviews"}
      >
        <EnrollmentCallReviewsTab
          start={callReviewStart}
          end={callReviewEnd}
          setStart={setCallReviewStart}
          setEnd={setCallReviewEnd}
          loading={callReviewsQuery.isLoading}
          error={
            callReviewsQuery.error instanceof Error
              ? callReviewsQuery.error.message
              : callReviewsQuery.error
                ? "Failed"
                : ""
          }
          rows={callReviewsQuery.data ?? []}
        />
      </section>
    </DashboardsPageFrame>
  );
}
